import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'src/main/store/store'
import { StoreStateInterface } from 'src/main/interfaces/redux/redux'
import {
  dismissed,
  dismissAllPolling,
  startPolling,
} from 'src/main/store-models/polling-status/polling-status'
import { PollingEntry, PollingFactory } from 'src/main/store-models/polling-status/types'
import * as S from './styles'

const RING_R = 20
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R

interface Props {
  factories: PollingFactory[]
}

const statusIcon = (uiStatus: PollingEntry['uiStatus'], fontSize = 14): React.ReactNode => {
  if (uiStatus === 'loading') {
    return (
      <svg
        viewBox="0 0 24 24"
        width={fontSize}
        height={fontSize}
        style={{ animation: 'pollingIconSpin 1s linear infinite', fill: '#1890ff', flexShrink: 0 }}
      >
        <style>{'@keyframes pollingIconSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'}</style>
        <path d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8V2z" />
      </svg>
    )
  }
  if (uiStatus === 'success') {
    return (
      <svg viewBox="0 0 24 24" width={fontSize} height={fontSize} style={{ fill: '#52c41a', flexShrink: 0 }}>
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 14.414-4.707-4.707 1.414-1.414L11 13.586l6.293-6.293 1.414 1.414L11 16.414z" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" width={fontSize} height={fontSize} style={{ fill: '#ff4d4f', flexShrink: 0 }}>
      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.707 13.293-1.414 1.414L12 13.414l-3.293 3.293-1.414-1.414L10.586 12 7.293 8.707l1.414-1.414L12 10.586l3.293-3.293 1.414 1.414L13.414 12l3.293 3.293z" />
    </svg>
  )
}

const getIconColor = (hasError: boolean, hasSuccess: boolean): string => {
  if (hasError) return '#ff4d4f'
  if (hasSuccess) return '#52c41a'
  return '#1890ff'
}

const getRingColor = (hasError: boolean, hasSuccess: boolean): string => {
  if (hasError) return '#ff4d4f'
  if (hasSuccess) return '#52c41a'
  return '#1890ff'
}

const getTitleAttr = (hasLoading: boolean, loadingCount: number, hasError: boolean): string => {
  if (hasLoading) return `${loadingCount} tarefa${loadingCount > 1 ? 's' : ''} em andamento`
  if (hasError) return 'Tarefa(s) com erro'
  return 'Todas as tarefas concluídas'
}

// Server sends UTC timestamps without 'Z'; our generated ones (error fallback) already have 'Z'
const parseApiDate = (s: string): number => {
  const normalized = s.replace(/(\.\d{3})\d*/, '$1')
  const hasTimezone = normalized.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(normalized)
  return new Date(hasTimezone ? normalized : `${normalized}Z`).getTime()
}

const formatSeconds = (totalSec: number): string => {
  const s = Math.max(0, totalSec)
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

const getElapsedDisplay = (task: PollingEntry, now: number): string | null => {
  const { iniciadoEm, finalizadoEm } = task.meta
  if (!iniciadoEm) return null
  const startMs = parseApiDate(iniciadoEm)
  if (Number.isNaN(startMs)) return null
  if (task.uiStatus === 'loading') {
    return formatSeconds(Math.floor((now - startMs) / 1000))
  }
  if (!finalizadoEm) return null
  const endMs = parseApiDate(finalizadoEm)
  if (Number.isNaN(endMs)) return null
  return formatSeconds(Math.floor((endMs - startMs) / 1000))
}

export const PollingStatusIndicator = ({ factories }: Props): JSX.Element | null => {
  const dispatch = useDispatch<AppDispatch>()
  const tasks = useSelector((state: StoreStateInterface) => state.pollingStatus.tasks)
  const [open, setOpen] = useState(false)
  const [, setTick] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const resumed = useRef(false)

  useEffect(() => {
    if (resumed.current) return
    resumed.current = true
    tasks
      .filter((t) => t.uiStatus === 'loading')
      .forEach((task) => {
        const found = factories.find((f) => f.id === task.id)
        if (!found) return
        dispatch(
          startPolling({
            id: task.id,
            title: task.title,
            fetchStatus: found.factory(task.params),
            params: task.params,
            intervalMs: task.intervalMs,
          }),
        )
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('click', handleOutsideClick)
    return () => document.removeEventListener('click', handleOutsideClick)
  }, [])

  const [now, setNow] = useState(() => Date.now())
  const hasTimedTask = tasks.some((t) => t.uiStatus === 'loading' && t.meta.iniciadoEm)
  useEffect(() => {
    if (!hasTimedTask) return undefined
    const handle = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(handle)
  }, [hasTimedTask])

  if (tasks.length === 0) return null

  const loadingCount = tasks.filter((t) => t.uiStatus === 'loading').length
  const totalCount = tasks.length
  const hasError = tasks.some((t) => t.uiStatus === 'error')
  const hasSuccess = tasks.some((t) => t.uiStatus === 'success')
  const hasLoading = loadingCount > 0
  const allLoading = loadingCount === totalCount

  const spinning = hasLoading

  let arcFraction: number
  if (!hasLoading) {
    arcFraction = 1
  } else if (allLoading) {
    arcFraction = 0.75
  } else {
    arcFraction = loadingCount / totalCount
  }
  const strokeDashoffset = RING_CIRCUMFERENCE - arcFraction * RING_CIRCUMFERENCE

  const ringColor = getRingColor(hasError, hasSuccess)
  const iconColor = getIconColor(hasError, hasSuccess)
  const titleAttr = getTitleAttr(hasLoading, loadingCount, hasError)

  return (
    <S.Wrapper ref={wrapperRef}>
      <S.IconButton onClick={() => setOpen((prev) => !prev)} title={titleAttr}>
        <S.RingSvg $spinning={spinning} viewBox="0 0 48 48">
          <circle cx="24" cy="24" r={RING_R} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="2" />
          <circle
            cx="24"
            cy="24"
            r={RING_R}
            fill="none"
            stroke={ringColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </S.RingSvg>

        <svg
          viewBox="0 0 24 24"
          width={18}
          height={18}
          style={{ fill: iconColor, position: 'relative', zIndex: 1 }}
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
        </svg>
      </S.IconButton>

      {open && (
        <S.Dropdown>
          <S.DropdownHeader>
            <S.DropdownTitle>Tarefas em andamento</S.DropdownTitle>
            <S.ClearAllButton
              onClick={() => {
                dispatch(dismissAllPolling())
                setOpen(false)
              }}
            >
              Limpar todos
            </S.ClearAllButton>
          </S.DropdownHeader>

          <S.TaskList>
            {tasks.map((task) => (
              <S.TaskItem key={task.id}>
                <S.TaskHeader>
                  <S.TaskTitleRow>
                    {statusIcon(task.uiStatus)}
                    <S.TaskTitle>{task.title}</S.TaskTitle>
                  </S.TaskTitleRow>
                  {task.uiStatus !== 'loading' && (
                    <S.DismissButton
                      onClick={() => dispatch(dismissed(task.id))}
                      title="Remover"
                    >
                      ✕
                    </S.DismissButton>
                  )}
                </S.TaskHeader>

                {task.uiStatus === 'loading' && (
                  <S.ProgressBar>
                    <S.ProgressFill />
                  </S.ProgressBar>
                )}

                {task.message && <S.TaskMessage>{task.message}</S.TaskMessage>}

                {getElapsedDisplay(task, now) !== null && (
                  <S.MetaRow>
                    <strong>{getElapsedDisplay(task, now)}</strong>
                  </S.MetaRow>
                )}

                {Object.entries(task.meta)
                  .filter(([key]) => key !== 'iniciadoEm' && key !== 'finalizadoEm' && key !== 'jobId')
                  .map(([key, value]) => (
                    <S.MetaRow key={key}>
                      {key}
                      :
                      {' '}
                      <strong>{value}</strong>
                    </S.MetaRow>
                  ))}

                {task.uiStatus === 'success' && task.meta.jobId && (() => {
                  const factory = factories.find((f) => f.id === task.id)
                  return factory?.download ? (
                    <S.DownloadButton
                      type="button"
                      onClick={() => {
                        factory.download!(task.params)
                        dispatch(dismissed(task.id))
                      }}
                    >
                      ↓ Baixar arquivo
                    </S.DownloadButton>
                  ) : null
                })()}

                {task.errors.length > 0 && (
                  <S.ErrorList>
                    <S.ErrorTitle>
                      Erros (
                      {task.errors.length}
                      ):
                    </S.ErrorTitle>
                    {task.errors.slice(0, 10).map((err, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <S.ErrorItem key={idx}>
                        {'• '}
                        {err}
                      </S.ErrorItem>
                    ))}
                    {task.errors.length > 10 && (
                      <S.ErrorItem>
                        ...e mais
                        {' '}
                        {task.errors.length - 10}
                      </S.ErrorItem>
                    )}
                  </S.ErrorList>
                )}
              </S.TaskItem>
            ))}
          </S.TaskList>
        </S.Dropdown>
      )}
    </S.Wrapper>
  )
}
